import React, { memo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    getBackgroundPhoto,
    getDefaultBackgroundPhoto,
} from '../../utils/photos';
import './AuctionImage.css';

const AuctionImage = memo(({ showcase, photo, link, useDefaultStyling, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const containerRef = useRef(null);

    // Lazy load using IntersectionObserver
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '100px' } // Start loading 100px before visible
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Track photo URL to detect changes
    const photoUrl = photo && photo.url;

    // Reset loaded state when photo changes
    useEffect(() => {
        if (photoUrl) {
            setIsLoaded(false);
        }
    }, [photoUrl]);

    // Preload image when visible
    useEffect(() => {
        if (isVisible && photoUrl) {
            const img = new Image();
            img.onload = () => setIsLoaded(true);
            img.src = photoUrl;
        } else if (isVisible) {
            setIsLoaded(true); // No photo, just show default
        }
    }, [isVisible, photoUrl]);

    let isDefault = false;
    let imageStyle = isVisible ? getBackgroundPhoto(photo) : null;

    if (!imageStyle) {
        isDefault = true;
        imageStyle = isVisible ? getDefaultBackgroundPhoto() : null;
    }

    const baseClass = showcase ? 'al-hero-image--showcase' : 'al-hero-image';
    const defaultClass = isDefault && useDefaultStyling ? 'al-hero-image--default' : '';
    const loadingClass = !isLoaded ? 'al-hero-image--loading' : '';

    const ImageContent = () => (
        <React.Fragment>
            {/* Blurred background layer */}
            <div
                className="al-hero-image-bg"
                style={imageStyle}
            />
            {/* Sharp foreground image */}
            <div
                className="al-hero-image-fg"
                style={imageStyle}
            />
        </React.Fragment>
    );

    return (
        <div ref={containerRef} className={`${baseClass} ${defaultClass} ${loadingClass}`.trim()}>
            {link ? (
                <Link to={link} className="al-hero-image-link">
                    <ImageContent />
                </Link>
            ) : (
                <ImageContent />
            )}
            {children}
        </div>
    );
});

AuctionImage.displayName = 'AuctionImage';

export default AuctionImage;
